import { httpsCallable } from 'firebase/functions'
import { functions } from 'fb/index'
import { apiClient } from 'api/client'
import { isRestStaffEnabled } from 'api/featureFlags'
import { toLegacyCallableError } from 'api/errorCompat'

/**
 * スタッフ招待 / パスワード設定 Cloud Functions の REST 化ラッパー。
 */

/**
 * Firestore の DocumentReference 等、JSON 化できない値を落として
 * REST へ送れる plain object に変換する。失敗時は元の値を返す。
 */
const jsonSafe = (x: unknown): unknown => {
  try {
    return JSON.parse(JSON.stringify(x))
  } catch {
    return x
  }
}

type InviteStaffParams = {
  email: string
  isResend?: boolean
  addStaffOnly?: boolean
  shopId?: string | null
  storeId?: string | null
  shopData?: unknown
  storeData?: unknown
  isBillPayment?: boolean
  dsRole?: number
}

export const inviteStaff = async (
  params: InviteStaffParams,
): Promise<{ data: { status: string; message: string } }> => {
  const {
    email,
    isResend,
    addStaffOnly,
    shopId,
    storeId,
    shopData,
    storeData,
    isBillPayment,
    dsRole,
  } = params
  if (isRestStaffEnabled()) {
    try {
      const res = await apiClient.post('/v1/identities/staff/invitations', {
        email,
        is_resend: isResend ?? false,
        add_staff_only: addStaffOnly ?? false,
        is_bill_payment: isBillPayment ?? false,
        ...(shopId ? { shop_document_id: shopId } : {}),
        ...(storeId ? { store_document_id: storeId } : {}),
        ...(shopData ? { shop_data: jsonSafe(shopData) } : {}),
        ...(storeData ? { store_data: jsonSafe(storeData) } : {}),
        ...(dsRole !== undefined ? { ds_role: dsRole } : {}),
      })
      return { data: { status: res.data.status, message: res.data.message } }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(functions, 'inviteDaserunoStaffs')
  return callable({
    email,
    is_resend: isResend,
    addStaffOnly,
    shopId,
    storeId,
    shopData,
    storeData,
    isBillPayment,
    dsRole,
  }) as Promise<{ data: { status: string; message: string } }>
}

type SetPasswordParams = {
  token: string
  password: string
}

export const setPassword = async (
  params: SetPasswordParams,
): Promise<{ data: { status: string; message: string } }> => {
  const { token, password } = params
  if (isRestStaffEnabled()) {
    try {
      const res = await apiClient.post('/v1/identities/staff/set-password', {
        token,
        password,
      })
      return { data: { status: res.data.status, message: res.data.message } }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(functions, 'setPasswordDaserunoStaffs')
  return callable({ token, password }) as Promise<{
    data: { status: string; message: string }
  }>
}

type UpdateRoleParams = {
  staffId: string
  storeId: string
  role: number
}

export const updateRole = async (
  params: UpdateRoleParams,
): Promise<{ data: { status: string; message: string } }> => {
  const { staffId, storeId, role } = params
  if (isRestStaffEnabled()) {
    try {
      const res = await apiClient.patch(
        `/v1/identities/staff/${encodeURIComponent(staffId)}/role`,
        { store_document_id: storeId, role },
      )
      return { data: { status: res.data.status, message: res.data.message } }
    } catch (e) {
      throw toLegacyCallableError(e)
    }
  }
  const callable = httpsCallable(functions, 'apiUpdateDaserunoRole')
  return callable({ staffId, storeId, role }) as Promise<{
    data: { status: string; message: string }
  }>
}

// ========================================
// Phase 3: メンバー一覧取得 (READ)
// ========================================

export interface ApiStaffMember {
  uid: string
  email: string | null
  /** daseruno 店舗権限。9 = 管理者、5 = 閲覧 */
  ds_role: number
  invitation_expired_at: string | null
}

/**
 * GET /v1/identities/staff/members?store_document_id=...
 *
 * 指定 store のメンバー一覧を取得する。Web スライス getStaffs の REST パスで使用。
 * フラグ判定 (isRestStaffEnabled) は呼び出し側 slice で行う。
 */
export const fetchStaffMembers = async (
  storeId: string,
): Promise<ApiStaffMember[]> => {
  const res = await apiClient.get<{ members: ApiStaffMember[] }>(
    '/v1/identities/staff/members',
    { params: { store_document_id: storeId } },
  )
  return res.data.members
}

// ========================================
// Phase 3: 認証ユーザーのスタッフ集約取得 (getStaff の REST 版)
// ========================================

export interface ApiStaffMe {
  uid: string
  email: string | null
  is_admin: boolean
  is_daseruno_deleted: boolean
  role: number | null
  ds_role: number | null
  image_url: string | null
  is_standard_plan: boolean
  shop_document_id: string | null
  shop_name: string | null
  shop_address: string | null
  responsible_person: string | null
  store_document_id: string | null
  store_name: string | null
  store_address: string | null
  manufacturer_address: string | null
  store_phone_number: string | null
  personal_phone_number: string | null
  invitation_expired_at: string | null
}

/**
 * GET /v1/identities/staff/me
 *
 * 認証ユーザー (Firebase ID Token) のスタッフ集約を取得する。
 * uid は API 側で token から導出するため引数不要。
 * super-admin 用に adminShopId / adminStoreId を任意で渡す。
 * フラグ判定 (isRestStaffEnabled) は呼び出し側 slice で行う。該当なしは null。
 */
export const fetchStaffMe = async (params?: {
  adminShopId?: string | null
  adminStoreId?: string | null
}): Promise<ApiStaffMe | null> => {
  const query: Record<string, string> = {}
  if (params?.adminShopId) query.admin_shop_id = params.adminShopId
  if (params?.adminStoreId) query.admin_store_id = params.adminStoreId
  const res = await apiClient.get<ApiStaffMe | null>(
    '/v1/identities/staff/me',
    {
      params: query,
    },
  )
  return res.data ?? null
}
